import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, Upload, Link2, Check, X, 
  Loader2, Download, Eye, AlertCircle, RefreshCw, FileUp, Sun
} from 'lucide-react';

export default function AdminImportPage({ embedded = false }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Google Sheets state
  const [sheetUrl, setSheetUrl] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [importType, setImportType] = useState('students');
  const [importResult, setImportResult] = useState(null);
  const [templates, setTemplates] = useState(null);
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/import/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handlePreviewSheet = async () => {
    if (!sheetUrl) {
      toast.error('Please enter a Google Sheets URL');
      return;
    }
    
    setLoading(true);
    setPreviewData(null);
    
    try {
      const response = await api.post('/sheets/preview', { url: sheetUrl });
      setPreviewData(response.data);
      
      // Auto-detect import type
      if (response.data.data_type_suggestions) {
        setImportType(response.data.data_type_suggestions);
      }
      
      toast.success(`Loaded ${response.data.row_count} rows from sheet`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load sheet. Make sure it\'s published to web.');
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromSheet = async () => {
    if (!sheetUrl) {
      toast.error('Please enter a Google Sheets URL');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const response = await api.post('/sheets/import', {
        url: sheetUrl,
        data_type: importType
      });
      
      setImportResult(response.data);
      
      if (response.data.created > 0 || response.data.updated > 0) {
        toast.success(`Imported: ${response.data.created} created, ${response.data.updated} updated`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handlePreviewFile = async () => {
    if (!selectedFile) { toast.error('Please select a file'); return; }
    setLoading(true);
    setPreviewData(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('import_type', importType);
      const response = await api.post('/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreviewData({
        row_count: response.data.total_rows,
        columns: response.data.columns_found,
        preview: response.data.preview,
        unique_rows: response.data.unique_rows,
        potential_duplicates: response.data.potential_duplicates,
      });
      toast.success(`Preview: ${response.data.total_rows} rows, ${response.data.potential_duplicates} potential duplicates`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await api.post(`/import/${importType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      
      if (response.data.created > 0 || response.data.updated > 0) {
        toast.success(`Imported: ${response.data.created} created, ${response.data.updated} updated`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await api.get(`/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exported ${type} data`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-import-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">Data Import & Export</h1>
        <p className="text-muted-foreground">Import data from Google Sheets or CSV files, export data to CSV</p>
      </div>

      <Tabs defaultValue="google-sheets" className="w-full">
        <TabsList>
          <TabsTrigger value="google-sheets">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Google Sheets
          </TabsTrigger>
          <TabsTrigger value="file-upload">
            <FileUp className="h-4 w-4 mr-2" />
            File Upload
          </TabsTrigger>
          <TabsTrigger value="export">
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </TabsTrigger>
          <TabsTrigger value="sawyer">
            <RefreshCw className="h-4 w-4 mr-2" />
            Sawyer Import
          </TabsTrigger>
          <TabsTrigger value="summer-camps">
            <Sun className="h-4 w-4 mr-2" />
            Summer Camps
          </TabsTrigger>
        </TabsList>

        {/* Download Templates Banner */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">📋 Download Import Templates</h4>
          <p className="text-sm text-blue-700 mb-3">Pre-formatted CSV templates with sample data and all required columns. Fill in your data and upload.</p>
          <div className="flex flex-wrap gap-3">
            <a href="/templates/course_import_template.csv" download className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-lg text-sm font-semibold text-blue-700 hover:bg-blue-100 transition-colors">
              <Download className="h-4 w-4" /> Course Import Template (10 samples)
            </a>
            <a href="/templates/summer_camp_import_template.csv" download className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-orange-300 rounded-lg text-sm font-semibold text-orange-700 hover:bg-orange-100 transition-colors">
              <Sun className="h-4 w-4" /> Summer Camp Template (10 samples)
            </a>
            <a href="/templates/IMPORT_GUIDE.md" target="_blank" className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors">
              <Eye className="h-4 w-4" /> Import Guide & Column Reference
            </a>
          </div>
        </div>

        {/* Google Sheets Import */}
        <TabsContent value="google-sheets" className="space-y-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Import from Google Sheets
              </CardTitle>
              <CardDescription>
                Import data from a public Google Sheet. The sheet must be published to the web.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">How to Import from Google Sheets</h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Open your Google Sheet</li>
                  <li>Go to File → Share → Publish to web</li>
                  <li>Select "Entire Document" and "Comma-separated values (.csv)"</li>
                  <li>Click Publish and copy the URL</li>
                  <li>Paste the URL below</li>
                </ol>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sheet-url">Google Sheets URL</Label>
                    <Input
                      id="sheet-url"
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      value={sheetUrl}
                      onChange={(e) => setSheetUrl(e.target.value)}
                      data-testid="sheet-url-input"
                    />
                  </div>
                  <div>
                    <Label>Data Type</Label>
                    <Select value={importType} onValueChange={setImportType}>
                      <SelectTrigger data-testid="import-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="students">Students</SelectItem>
                        <SelectItem value="parents">Parents</SelectItem>
                        <SelectItem value="courses">Courses</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={handlePreviewSheet} disabled={loading} variant="outline" data-testid="preview-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    Preview Data
                  </Button>
                  <Button onClick={handleImportFromSheet} disabled={importing || !sheetUrl} data-testid="import-btn">
                    {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Import Data
                  </Button>
                </div>
              </div>

              {/* Preview Data */}
              {previewData && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Badge variant="secondary">
                      {previewData.row_count} rows found
                    </Badge>
                    {previewData.data_type_suggestions && (
                      <Badge>
                        Detected: {previewData.data_type_suggestions}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="border rounded-lg overflow-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {previewData.columns?.map((col, i) => (
                            <TableHead key={i} className="whitespace-nowrap">{col}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.preview?.map((row, rowIdx) => (
                          <TableRow key={rowIdx}>
                            {previewData.columns?.map((col, cellIdx) => (
                              <TableCell key={cellIdx} className="whitespace-nowrap">
                                {row[col] || '-'}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* File Upload Import */}
        <TabsContent value="file-upload" className="space-y-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileUp className="h-5 w-5" />
                Upload CSV File
              </CardTitle>
              <CardDescription>
                Upload a CSV or JSON file to import data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Column Templates */}
              {templates && (
                <div className="space-y-4">
                  <h4 className="font-medium">Required Columns for Each Data Type</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(templates).map(([type, config]) => (
                      <div key={type} className="border rounded-lg p-4">
                        <h5 className="font-medium capitalize mb-2">{type}</h5>
                        <p className="text-xs text-muted-foreground mb-2">{config.description}</p>
                        <div className="text-xs">
                          <p className="font-medium text-green-700">Required:</p>
                          <p className="text-muted-foreground">{config.required_columns?.join(', ')}</p>
                          {config.optional_columns?.length > 0 && (
                            <>
                              <p className="font-medium text-blue-700 mt-2">Optional:</p>
                              <p className="text-muted-foreground">{config.optional_columns?.join(', ')}</p>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="file-upload">Select File (CSV or JSON)</Label>
                    <Input
                      id="file-upload"
                      type="file"
                      accept=".csv,.json"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      data-testid="file-input"
                    />
                  </div>
                  <div>
                    <Label>Data Type</Label>
                    <Select value={importType} onValueChange={setImportType}>
                      <SelectTrigger data-testid="file-import-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="students">Students</SelectItem>
                        <SelectItem value="parents">Parents</SelectItem>
                        <SelectItem value="courses">Courses</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={handlePreviewFile} disabled={loading || !selectedFile}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    Step 1: Preview
                  </Button>
                  <Button onClick={handleFileUpload} disabled={importing || !selectedFile} data-testid="upload-btn">
                    {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Step 2: Import
                  </Button>
                </div>

                {/* Preview Data for file upload */}
                {previewData && (
                  <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold flex items-center gap-2"><Eye className="h-4 w-4" /> Preview</h4>
                    <div className="flex gap-4 text-sm">
                      <Badge variant="secondary">{previewData.row_count} total rows</Badge>
                      {previewData.unique_rows && <Badge variant="outline">{previewData.unique_rows} unique</Badge>}
                      {previewData.potential_duplicates > 0 && <Badge variant="destructive">{previewData.potential_duplicates} duplicates</Badge>}
                    </div>
                    {previewData.columns && (
                      <p className="text-xs text-muted-foreground">Columns: {previewData.columns.join(', ')}</p>
                    )}
                    {previewData.preview?.length > 0 && (
                      <div className="overflow-auto max-h-[200px] border rounded bg-white">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {(previewData.columns || Object.keys(previewData.preview[0])).slice(0, 6).map(col => (
                                <TableHead key={col} className="text-xs whitespace-nowrap">{col}</TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {previewData.preview.slice(0, 10).map((row, i) => (
                              <TableRow key={i}>
                                {(previewData.columns || Object.keys(row)).slice(0, 6).map(col => (
                                  <TableCell key={col} className="text-xs truncate max-w-[150px]">{row[col] || '-'}</TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Data */}
        <TabsContent value="export" className="space-y-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Export Data
              </CardTitle>
              <CardDescription>
                Download data as CSV files
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Button onClick={() => handleExport('students')} variant="outline" data-testid="export-students-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export Students
                </Button>
                <Button onClick={() => handleExport('parents')} variant="outline" data-testid="export-parents-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export Parents
                </Button>
                <Button onClick={() => handleExport('courses')} variant="outline" data-testid="export-courses-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export Courses
                </Button>
                <Button onClick={() => handleExport('enrollments')} variant="outline" data-testid="export-enrollments-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export Enrollments
                </Button>
                <Button onClick={() => handleExport('attendance')} variant="outline" data-testid="export-attendance-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export Attendance
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sawyer Platform Import */}
        <TabsContent value="sawyer" className="space-y-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Import from Sawyer
              </CardTitle>
              <CardDescription>
                Import parents, children, and enrollments from your Sawyer booking export. 
                Duplicate session rows are automatically deduplicated.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">How to Export from Sawyer</h4>
                <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                  <li>Log into your Sawyer admin dashboard</li>
                  <li>Go to Reports → Booking Report</li>
                  <li>Select date range and export as CSV or Google Sheet</li>
                  <li>Paste the Google Sheets URL below, or upload the CSV file</li>
                  <li><strong>Step 1:</strong> Click "Preview" to see dedup stats and sample data</li>
                  <li><strong>Step 2:</strong> Review, then click "Import" to proceed</li>
                </ol>
              </div>

              {/* Input Section */}
              <div className="space-y-4">
                <div>
                  <Label>Google Sheets URL</Label>
                  <Input
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    value={sheetUrl}
                    onChange={(e) => { setSheetUrl(e.target.value); setPreviewData(null); setImportResult(null); }}
                    className="mt-1"
                  />
                </div>

                <div className="text-center text-sm text-muted-foreground">— or upload a CSV file —</div>

                <div>
                  <Label>Upload Sawyer CSV Export</Label>
                  <Input
                    type="file"
                    accept=".csv"
                    className="mt-1"
                    id="sawyer-file-input"
                    onChange={() => { setPreviewData(null); setImportResult(null); }}
                    disabled={importing}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const fileInput = document.getElementById('sawyer-file-input');
                      const file = fileInput?.files?.[0];
                      if (!sheetUrl && !file) { toast.error('Enter a URL or upload a CSV'); return; }
                      setImporting(true); setPreviewData(null);
                      try {
                        const fd = new FormData();
                        if (file) fd.append('file', file);
                        else fd.append('url', sheetUrl);
                        const res = await api.post('/import/sawyer/preview', fd);
                        setPreviewData(res.data);
                        toast.success(`Preview loaded: ${res.data.total_raw_rows} rows, ${res.data.unique_enrollments} unique after dedup`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Preview failed');
                      } finally { setImporting(false); }
                    }}
                    disabled={importing}
                    className="flex-1"
                  >
                    {importing && !importResult ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    Step 1: Preview Data
                  </Button>
                  <Button
                    onClick={async () => {
                      const fileInput = document.getElementById('sawyer-file-input');
                      const file = fileInput?.files?.[0];
                      if (!sheetUrl && !file) { toast.error('Enter a URL or upload a CSV'); return; }
                      if (!previewData && !window.confirm('Import without preview? This will process all rows.')) return;
                      setImporting(true);
                      try {
                        const fd = new FormData();
                        if (file) fd.append('file', file);
                        else fd.append('url', sheetUrl);
                        const res = await api.post('/import/sawyer', fd);
                        setImportResult(res.data);
                        toast.success(res.data.summary);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Import failed');
                      } finally { setImporting(false); }
                    }}
                    disabled={importing}
                    className="flex-1 btn-primary"
                  >
                    {importing && importResult ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Step 2: Import Data
                  </Button>
                </div>
              </div>

              {/* PREVIEW RESULTS */}
              {previewData && !importResult && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg flex items-center gap-2"><Eye className="h-5 w-5" /> Preview — Deduplication Summary</h4>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-2xl font-bold">{previewData.total_raw_rows?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Total Raw Rows</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-600">{previewData.duplicates_removed?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Duplicates Removed</p>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-yellow-600">{previewData.canceled_skipped}</p>
                      <p className="text-xs text-muted-foreground">Canceled Skipped</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{previewData.unique_enrollments?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Unique Enrollments</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-blue-600">{previewData.unique_parents}</p>
                      <p className="text-xs text-muted-foreground">Unique Parents</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-purple-600">{previewData.unique_children}</p>
                      <p className="text-xs text-muted-foreground">Unique Children</p>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-indigo-600">{previewData.unique_activities || previewData.unique_courses}</p>
                      <p className="text-xs text-muted-foreground">Unique Activities</p>
                    </div>
                  </div>

                  {/* Course Matching Stats */}
                  {(previewData.matched_courses !== undefined || previewData.new_courses !== undefined) && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                        <p className="text-xl font-bold text-green-600">{previewData.matched_courses || 0}</p>
                        <p className="text-xs text-muted-foreground">Matched to Existing Courses</p>
                      </div>
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-center">
                        <p className="text-xl font-bold text-orange-600">{previewData.new_courses || 0}</p>
                        <p className="text-xs text-muted-foreground">Will Create New Courses</p>
                      </div>
                    </div>
                  )}

                  {/* Course Mapping Table */}
                  {previewData.course_mapping?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Course Mapping — Sawyer Activity → Your Course</h4>
                      <div className="overflow-auto max-h-[250px] border rounded-lg">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Sawyer Activity Name</TableHead>
                              <TableHead className="text-xs">Matched Course</TableHead>
                              <TableHead className="text-xs">Match Type</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {previewData.course_mapping.map((m, i) => (
                              <TableRow key={i} className={m.will_create ? 'bg-orange-50' : 'bg-green-50'}>
                                <TableCell className="text-xs font-medium">{m.sawyer_name?.split('|')[0]?.trim()}</TableCell>
                                <TableCell className="text-xs">{m.matched_course || <span className="text-orange-600 font-semibold">→ NEW COURSE</span>}</TableCell>
                                <TableCell className="text-xs">
                                  {m.match_type === 'exact' && <Badge className="bg-green-100 text-green-700 text-[10px]">Exact</Badge>}
                                  {m.match_type === 'starts_with' && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Starts With</Badge>}
                                  {m.match_type === 'fuzzy' && <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">Fuzzy</Badge>}
                                  {m.match_type === 'partial' && <Badge className="bg-purple-100 text-purple-700 text-[10px]">Partial</Badge>}
                                  {m.match_type === 'no_match' && <Badge className="bg-orange-100 text-orange-700 text-[10px]">New</Badge>}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {previewData.duplicates_removed > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                      <strong>Deduplication:</strong> Sawyer exports one row per session. For a 4-session course, each student appears 4 times. 
                      We group by <em>customer email + child name + activity name</em> to create one enrollment per student per course.
                      <strong> {previewData.duplicates_removed.toLocaleString()} duplicate rows will be skipped.</strong>
                    </div>
                  )}

                  {/* Preview Table */}
                  <div>
                    <h4 className="font-semibold mb-2">First 10 Unique Enrollments (Preview)</h4>
                    <div className="overflow-auto max-h-[300px] border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Parent Email</TableHead>
                            <TableHead className="text-xs">Child</TableHead>
                            <TableHead className="text-xs">Sawyer Activity</TableHead>
                            <TableHead className="text-xs">→ Matched Course</TableHead>
                            <TableHead className="text-xs">Location</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {previewData.preview?.map((row, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs">{row.customer_email}</TableCell>
                              <TableCell className="text-xs">{row.child_name}</TableCell>
                              <TableCell className="text-xs">{row.activity_name?.split('|')[0]?.trim()}</TableCell>
                              <TableCell className="text-xs font-medium">
                                {row.matched_course ? (
                                  <span className="text-green-700">{row.matched_course}</span>
                                ) : (
                                  <span className="text-orange-600">→ New</span>
                                )}
                              </TableCell>
                              <TableCell className="text-xs">{row.location_name}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <div className="text-sm text-muted-foreground">
                    Columns detected: {previewData.columns_found?.join(', ')}
                  </div>
                </div>
              )}

              {/* IMPORT RESULTS */}
              {importResult && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg flex items-center gap-2">
                    {importResult.total_errors === 0 ? <Check className="h-5 w-5 text-green-600" /> : <AlertCircle className="h-5 w-5 text-yellow-600" />}
                    Import Complete
                  </h4>

                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm">{importResult.summary}</p>
                    {importResult.batch_id && (
                      <p className="text-xs text-muted-foreground mt-1">Batch ID: {importResult.batch_id}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-white border rounded-lg text-center">
                      <p className="text-xl font-bold text-green-600">{importResult.parents_created}</p>
                      <p className="text-xs text-muted-foreground">Parents Created</p>
                    </div>
                    <div className="p-3 bg-white border rounded-lg text-center">
                      <p className="text-xl font-bold text-purple-600">{importResult.learners_created}</p>
                      <p className="text-xs text-muted-foreground">Learners Created</p>
                    </div>
                    <div className="p-3 bg-white border rounded-lg text-center">
                      <p className="text-xl font-bold text-blue-600">{importResult.courses_created}</p>
                      <p className="text-xs text-muted-foreground">Courses Created</p>
                    </div>
                    <div className="p-3 bg-white border rounded-lg text-center">
                      <p className="text-xl font-bold text-cyan-600">{importResult.sections_created || 0}</p>
                      <p className="text-xs text-muted-foreground">Sections Created</p>
                    </div>
                    <div className="p-3 bg-white border rounded-lg text-center">
                      <p className="text-xl font-bold text-indigo-600">{importResult.enrollments_created}</p>
                      <p className="text-xs text-muted-foreground">Enrollments Created</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-center text-sm">
                    <div className="p-2 bg-gray-50 rounded">
                      <span className="font-medium">{importResult.total_raw_rows?.toLocaleString()}</span> raw rows
                    </div>
                    <div className="p-2 bg-red-50 rounded">
                      <span className="font-medium text-red-600">{importResult.duplicates_removed?.toLocaleString()}</span> duplicates removed
                    </div>
                    <div className="p-2 bg-yellow-50 rounded">
                      <span className="font-medium text-yellow-600">{importResult.canceled_skipped}</span> canceled skipped
                    </div>
                  </div>

                  {/* Errors */}
                  {importResult.errors?.length > 0 && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-semibold text-red-800 mb-2">{importResult.errors.length} Error(s)</h4>
                      <div className="space-y-2 max-h-48 overflow-auto">
                        {importResult.errors.map((err, idx) => (
                          <div key={idx} className="p-2 bg-white rounded border border-red-100 text-sm">
                            <p className="font-medium text-red-700">Row {err.row}: {err.title}</p>
                            <p className="text-xs text-red-600">{err.error}</p>
                            {err.fix && <p className="text-xs text-gray-500 mt-1 italic">💡 {err.fix}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <strong>Default password:</strong> All new parent accounts were created with password <code className="bg-amber-100 px-1 rounded">Welcome123!</code>. 
                    Parents should reset their password on first login.
                  </div>

                  {/* Bulk Delete Option */}
                  <div className="p-4 bg-gray-50 border rounded-lg">
                    <h4 className="font-semibold mb-2">Not accurate? Delete imported data</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      If the import results are not accurate, you can use the bulk delete on the Courses page to remove imported courses,
                      or use the Seed Data Manager to purge demo data.
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/courses'}>
                        Go to Courses (Bulk Delete)
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/learners'}>
                        Go to Learners
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-900 mb-2">What Gets Imported</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="text-amber-800"><strong>Parents:</strong> Email, name → User accounts (password: Welcome123!)</div>
                  <div className="text-amber-800"><strong>Children:</strong> Child name → Learner profiles linked to parent</div>
                  <div className="text-amber-800"><strong>Courses:</strong> Activity name → Matched or auto-created</div>
                  <div className="text-amber-800"><strong>Enrollments:</strong> Bookings → Enrollment records (canceled skipped)</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Summer Camps Import */}
        <TabsContent value="summer-camps" className="space-y-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sun className="h-5 w-5 text-orange-500" />
                Import Summer Camps
              </CardTitle>
              <CardDescription>
                Import summer camp schedule from CSV or Google Sheets. Creates courses and camp sessions with week numbers, locations, pricing, and before/after care options.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-900 mb-2">Required Columns</h4>
                <p className="text-sm text-orange-800 mb-2"><strong>title, week_number, start_date, end_date, location_name</strong></p>
                <h4 className="font-semibold text-orange-900 mb-2 mt-3">Optional Columns</h4>
                <p className="text-sm text-orange-700">description, skill_level, grade_bands, age_min, age_max, schedule_type (full_day/half_am/half_pm/extended), price, early_bird_price, early_bird_deadline, capacity, start_time, end_time, instructor_email, lunch_included, before_care, after_care, what_to_bring, camp_highlights, topic_tags, state_standards</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Google Sheets URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      value={sheetUrl}
                      onChange={(e) => setSheetUrl(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      onClick={async () => {
                        if (!sheetUrl) { toast.error('Enter a URL'); return; }
                        setImporting(true);
                        try {
                          const formData = new FormData();
                          formData.append('url', sheetUrl);
                          const res = await api.post('/import/summer-camps', formData);
                          setImportResult(res.data);
                          toast.success(`Summer camps imported! ${res.data.camps_created} created, ${res.data.camps_updated} updated`);
                        } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
                        finally { setImporting(false); }
                      }}
                      disabled={importing}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sun className="h-4 w-4 mr-2" />}
                      Import Camps
                    </Button>
                  </div>
                </div>
                <div className="text-center text-sm text-muted-foreground">— or upload CSV —</div>
                <div>
                  <Label>Upload Summer Camp CSV</Label>
                  <Input type="file" accept=".csv" className="mt-1"
                    onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      setImporting(true);
                      try {
                        const formData = new FormData(); formData.append('file', file);
                        const res = await api.post('/import/summer-camps', formData);
                        setImportResult(res.data);
                        toast.success(`Summer camps imported! ${res.data.camps_created} created`);
                      } catch (err) { toast.error('Import failed'); }
                      finally { setImporting(false); }
                    }}
                    disabled={importing}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Import Results */}
      {importResult && (
        <Card className={`card-dashboard ${importResult.total_errors === 0 ? 'border-green-500' : 'border-yellow-500'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {importResult.total_errors === 0 ? (
                <Check className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              )}
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{importResult.created || 0}</p>
                <p className="text-sm text-muted-foreground">Created</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{importResult.updated || 0}</p>
                <p className="text-sm text-muted-foreground">Updated</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{importResult.total_errors || 0}</p>
                <p className="text-sm text-muted-foreground">Errors</p>
              </div>
            </div>

            {importResult.default_password && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Default Password:</strong> {importResult.default_password}
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  New users were created with this password. They should reset it on first login.
                </p>
              </div>
            )}

            {importResult.errors?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Errors ({importResult.total_errors})</h4>
                <div className="max-h-40 overflow-auto border rounded-lg p-3 bg-red-50 space-y-2">
                  {importResult.errors.map((error, i) => (
                    <div key={i} className="text-sm text-red-600">
                      {typeof error === 'string' ? error : (
                        <>
                          <p className="font-medium">Row {error.row}: {error.title || 'Unknown'}</p>
                          <p className="text-xs">{error.error}</p>
                          {error.fix && <p className="text-xs text-gray-500 italic">💡 {error.fix}</p>}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
